import React from "react";
import { useLocation } from "react-router-dom";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Box from "@amzn/meridian/box";
import DatePicker from "@amzn/meridian/date-picker";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Select, { SelectOption } from "@amzn/meridian/select";
import { useNavigationControl } from "../../hooks/useNavigationControl";
import { ROUTES } from "../../utils/constants";

import "./StationAssignmentTabs.scss";

interface TabItem {
  id: string;
  label: string;
  path: string;
}

// Define the tabs for the station assignment workflow
const tabs: TabItem[] = [
  {
    id: "shift-roster",
    label: "1. Shift rotation",
    path: ROUTES.SHIFT_ROTATION_SHIFT_ROSTER,
  },
  {
    id: "assignments",
    label: "2. Assignments",
    path: ROUTES.SHIFT_ROTATION_ASSIGNMENTS,
  },
];

export interface StationAssignmentTabsProps {
  date?: string;
  shiftType?: string;
  selectedHalf?: string;
  onHalfChange?: (half: string) => void;
  children: React.ReactNode;
}

const StationAssignmentTabs: React.FC<StationAssignmentTabsProps> = ({
  date,
  shiftType,
  selectedHalf,
  onHalfChange,
  children,
}) => {
  const { navigate } = useNavigationControl();
  const location = useLocation();

  const currentTab =
    tabs.find((tab) => tab.path === location.pathname)?.id ?? tabs[0].id;

  const handleTabChange = React.useCallback(
    (tabId: string) => {
      const selectedTab = tabs.find((tab) => tab.id === tabId);
      if (selectedTab) {
        navigate(selectedTab.path);
      }
    },
    [navigate]
  );

  const shiftValue = shiftType === "night" ? "night" : "day";

  // Half dropdown
  const halfValue = selectedHalf ?? "firstHalfStationAssignment";
  const handleHalfSelectChange = (value: string) => {
    if (onHalfChange) {
      onHalfChange(value);
    }
  };

  // No-op handler for disabled controlled inputs
  const noop = () => {};

  return (
    <Box spacingInset="300" className="station-assignment-tabs">
      <Box className="station-assignment-header">
        <div className="header-left">
          <Heading type="h400" level={2}>
            Station assignment
          </Heading>
          <div className="header-controls">
            {date && (
              <DatePicker
                value={date}
                onChange={noop}
                disabled
                data-testid="header-date"
              />
            )}
            {shiftType && (
              <div className="header-shift-select">
                <Text type="b200" tag="label">Shift</Text>
                <Select
                  value={shiftValue}
                  onChange={noop}
                  disabled
                  data-testid="header-shift-select"
                >
                  <SelectOption value="day" label="Day shift" />
                  <SelectOption value="night" label="Night shift" />
                </Select>
              </div>
            )}
            <div className="header-half-select">
              <Select
                value={halfValue}
                onChange={handleHalfSelectChange}
                data-testid="header-half-select"
              >
                <SelectOption value="firstHalfStationAssignment" label="First half" />
                <SelectOption value="secondHalfStationAssignment" label="Second half" />
              </Select>
            </div>
          </div>
        </div>
        <Box className="header-actions">
        </Box>
      </Box>

      <Box className="tabs-container">
        <TabGroup
          fill="line"
          value={currentTab}
          onChange={handleTabChange}
          aria-labelledby="station-assignment-tabs"
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.id}
              value={tab.id}
              aria-controls={tab.id}
              id={tab.id}
            >
              {tab.label}
            </Tab>
          ))}
        </TabGroup>
      </Box>

      <div className="tab-content">{children}</div>
    </Box>
  );
};

export default StationAssignmentTabs;

import './StationAssignmentTabs.scss';